/**
 * `ApiError` builds its message as `API error <status>: <detail>`, where the
 * detail is the server's own sentence about what was wrong with the request.
 */
const API_ERROR_MESSAGE = /^(API error \d+): .*/s;

interface CapturedEvent {
  event?: string;
  properties: Record<string, unknown>;
}

function redactValue(value: unknown): unknown {
  return typeof value === "string"
    ? value.replace(API_ERROR_MESSAGE, "$1")
    : value;
}

function redactList(list: unknown): unknown {
  if (!Array.isArray(list)) return list;
  return (list as unknown[]).map((entry) => {
    if (typeof entry !== "object" || entry === null || !("value" in entry)) {
      return entry;
    }
    const carrier = entry as Record<string, unknown>;
    return { ...carrier, value: redactValue(carrier.value) };
  });
}

/**
 * Removes what the server said from a reported exception, keeping the status.
 *
 * Uncaught rejections of an `ApiError` carry its message, and a save can be
 * rejected precisely for quoting the draft back. The mutation path already
 * withholds that; this is the same rule for the failures nobody caught.
 *
 * Stack traces are left alone: they name code, not content.
 */
export function redactServerDetail<T extends CapturedEvent>(event: T): T {
  if (event.event !== "$exception") return event;

  const values = event.properties.$exception_values;
  const list = event.properties.$exception_list;
  const redactedValues = Array.isArray(values)
    ? values.map(redactValue)
    : values;
  const redactedList = redactList(list);

  const changed =
    JSON.stringify(redactedValues) !== JSON.stringify(values) ||
    JSON.stringify(redactedList) !== JSON.stringify(list);
  if (!changed) return event;

  return {
    ...event,
    properties: {
      ...event.properties,
      $exception_values: redactedValues,
      $exception_list: redactedList,
    },
  };
}
